import React, { useEffect, useState } from 'react';
import { api, moneyFull } from '../lib/api';
import { Landmark, ExternalLink, RefreshCw, Pencil, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// IOLTA (trust account) card — advisor spec #1, v2.
//
// Headline number is a MANUALLY-ENTERED QuickBooks IOLTA 3209 balance (with
// entry timestamp + who entered it + staleness indicator). We discovered the
// Google-sheet-derived "pending" figure was inflated because that sheet is
// actually a LawPay-wide inflow log, not a pure trust ledger. Un-classified
// rows are now shown as a data-hygiene signal instead of trust liability.
export default function IoltaCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftBal, setDraftBal] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/iolta')
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load IOLTA snapshot.'));
  };

  useEffect(() => { load(); }, []);

  const startEdit = () => {
    const cur = data?.manual_balance?.balance;
    setDraftBal(cur != null ? String(cur) : '');
    setDraftNote(data?.manual_balance?.note || '');
    setEditing(true);
    setErr('');
  };

  const save = async () => {
    const n = parseFloat(draftBal);
    if (!Number.isFinite(n) || n < 0 || n > 10_000_000) {
      setErr('Balance must be a positive number up to $10M.');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/iolta/manual-balance', { balance: n, note: draftNote.trim() || null });
      setEditing(false);
      setErr('');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save balance.');
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    if (!user?.is_admin) return;
    setSyncing(true);
    setErr('');
    try {
      await api.post('/integrations/iolta/sync');
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  if (!data) {
    return (
      <div data-testid="iolta-card" className="bg-white border-t-4 border-indie-cobalt p-6">
        <div className="label-editorial">IOLTA · Trust balance</div>
        <div className="mt-4 text-sm text-indie-ink/60">{err || 'Loading…'}</div>
      </div>
    );
  }

  const { snapshot, manual_balance, manual_balance_freshness } = data;
  const sheetUrl = snapshot?.spreadsheet_id
    ? `https://docs.google.com/spreadsheets/d/${snapshot.spreadsheet_id}/edit`
    : null;

  // Manual balance section --------------------------------------------------
  const bal = manual_balance?.balance;
  const enteredAt = manual_balance?.entered_at
    ? new Date(manual_balance.entered_at)
    : null;
  const enteredBy = manual_balance?.entered_by;
  const freshnessStatus = manual_balance_freshness?.status || 'unknown';
  const daysAgo = manual_balance_freshness?.days_ago;
  const freshPalette = {
    fresh:   { bg: 'bg-indie-evergreen/[0.08]', text: 'text-indie-evergreen', label: 'FRESH' },
    aging:   { bg: 'bg-indie-cobalt/[0.08]',    text: 'text-indie-cobalt',    label: 'AGING' },
    stale:   { bg: 'bg-indie-neg-dark/[0.08]',  text: 'text-indie-neg-dark',  label: 'STALE' },
    unknown: { bg: 'bg-indie-cream',            text: 'text-indie-ink/60',    label: 'NOT SET' },
  }[freshnessStatus];

  // Sheet-derived hygiene signal --------------------------------------------
  const unclassifiedCount = snapshot?.pending_unclassified_count || 0;
  const unclassifiedAmt = snapshot?.pending_unclassified || 0;
  const classifiedPending = snapshot?.pending_in_trust || 0;
  const classifiedPendingN = snapshot?.pending_count || 0;
  const submittedTotal = snapshot?.submitted_total || 0;
  const submittedN = snapshot?.submitted_count || 0;

  return (
    <div data-testid="iolta-card" className="bg-white border-t-4 border-indie-cobalt p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="label-editorial flex items-center gap-2">
            <Landmark size={14} className="text-indie-cobalt" /> IOLTA · Trust balance
          </div>
          <div className="text-xl font-semibold text-indie-navy mt-1">QuickBooks IOLTA 3209</div>
          <div className="text-[11px] text-indie-ink/60 mt-1">
            Authoritative source · manually updated until QBO integration lands
          </div>
        </div>
        <div className="flex items-center gap-2 no-print">
          {sheetUrl && (
            <a
              href={sheetUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="iolta-open-sheet"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-indie-cobalt hover:text-indie-navy"
            >
              LawPay ledger <ExternalLink size={12} />
            </a>
          )}
          {user?.is_admin && (
            <button
              type="button"
              onClick={sync}
              disabled={syncing}
              data-testid="iolta-sync-btn"
              className="inline-flex items-center gap-1 border border-indie-navy text-indie-navy px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold hover:bg-indie-navy hover:text-indie-cream disabled:opacity-60"
            >
              <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync sheet'}
            </button>
          )}
        </div>
      </div>

      {/* --- Manual QBO balance headline --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-1">
            <div className="label-editorial text-indie-ink/60">In-trust balance (QBO)</div>
            <div data-testid="iolta-freshness" className={`px-2 py-0.5 ${freshPalette.bg} ${freshPalette.text} text-[10px] font-semibold uppercase tracking-wider`}>
              {freshPalette.label}
            </div>
          </div>
          {editing ? (
            <div className="mt-1 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl text-indie-navy num-tabular">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draftBal}
                  onChange={(e) => setDraftBal(e.target.value)}
                  data-testid="iolta-balance-input"
                  placeholder="94234.64"
                  className="w-56 text-3xl font-semibold text-indie-navy border-b-2 border-indie-navy focus:outline-none num-tabular bg-transparent"
                  autoFocus
                />
              </div>
              <input
                type="text"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                data-testid="iolta-balance-note"
                placeholder="Optional note (e.g., 'Reconciled July 1 BS')"
                className="w-full border border-indie-navy/30 px-2 py-1 text-sm"
                maxLength={140}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="iolta-balance-save"
                  className="px-3 py-1.5 bg-indie-navy text-indie-yellow text-[11px] font-semibold uppercase tracking-wider disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Save balance'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setErr(''); }}
                  data-testid="iolta-balance-cancel"
                  className="p-1.5 text-indie-ink/60 hover:text-indie-navy"
                  aria-label="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mt-1">
                <div data-testid="iolta-balance-value" className="text-4xl font-semibold text-indie-navy num-tabular">
                  {bal != null ? moneyFull(bal) : '—'}
                </div>
                {user?.is_admin && (
                  <button
                    type="button"
                    onClick={startEdit}
                    data-testid="iolta-balance-edit"
                    className="p-1.5 text-indie-ink/50 hover:text-indie-navy"
                    aria-label="Edit balance"
                  >
                    <Pencil size={14} />
                  </button>
                )}
              </div>
              {enteredAt ? (
                <div className="text-xs text-indie-ink/60 mt-1.5 leading-snug">
                  Entered {enteredAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  {' '}({daysAgo != null ? `${daysAgo.toFixed(daysAgo < 1 ? 1 : 0)} day${daysAgo === 1 ? '' : 's'} ago` : ''})
                  {enteredBy ? ` by ${enteredBy.split('@')[0]}` : ''}
                  {manual_balance?.note ? ` · "${manual_balance.note}"` : ''}
                </div>
              ) : (
                <div className="text-xs text-indie-ink/60 mt-1.5">
                  {user?.is_admin ? 'Click the pencil to enter the current QBO IOLTA 3209 balance.'
                    : 'No balance recorded yet. Ask an admin to enter it.'}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-l md:pl-5 border-indie-navy/10">
          <div className="label-editorial text-indie-ink/60">Submitted YTD (from sheet)</div>
          <div className="text-2xl font-semibold text-indie-navy num-tabular mt-1">{moneyFull(submittedTotal)}</div>
          <div className="text-[11px] text-indie-ink/60 mt-1 num-tabular">
            {submittedN} filing{submittedN === 1 ? '' : 's'} sent to USPTO in 2026
          </div>
          {classifiedPending > 0 && (
            <div className="mt-2 text-[11px] text-indie-cobalt">
              {classifiedPendingN} classified filing{classifiedPendingN === 1 ? '' : 's'} pending: {moneyFull(classifiedPending)}
            </div>
          )}
        </div>
      </div>

      {/* --- Data-hygiene: unclassified LawPay rows --- */}
      {unclassifiedCount > 0 && (
        <div data-testid="iolta-hygiene" className="mt-5 pt-4 border-t border-indie-navy/10">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-indie-cobalt flex-shrink-0 mt-0.5" size={16} />
            <div className="flex-1">
              <div className="text-sm font-semibold text-indie-navy">
                {unclassifiedCount} LawPay entries need a Purpose classified
              </div>
              <div className="text-[11px] text-indie-ink/60 mt-0.5 leading-snug">
                {moneyFull(unclassifiedAmt)} of LawPay activity in the reconciliation sheet lacks a Purpose (col D). These are most likely earned fee payments routed to operating accounts — NOT trust liability — but leaving them unclassified obscures the true IOLTA picture. Add a Purpose in the sheet, or mark as Submitted if the work is done.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Recent activity (only rows with a Purpose show as classified) --- */}
      {snapshot?.recent_entries?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-indie-navy/10">
          <div className="label-editorial text-indie-ink/60 mb-2">Recent LawPay deposits</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px] num-tabular">
            {snapshot.recent_entries.slice(0, 6).map((e) => {
              let tag, tagCls;
              if (e.submitted) { tag = 'submitted'; tagCls = 'text-indie-evergreen'; }
              else if (e.purpose_classified) { tag = `pending · ${e.purpose}`; tagCls = 'text-indie-cobalt'; }
              else { tag = 'unclassified'; tagCls = 'text-indie-ink/50'; }
              return (
                <div key={`${e.row}-${e.client}`} className="flex items-center justify-between gap-3 border-b border-indie-navy/5 py-1">
                  <div className="truncate">
                    <span className="text-indie-navy font-medium">{e.client || 'Unknown'}</span>
                    <span className="text-indie-ink/50"> · {e.date_lawpay}</span>
                  </div>
                  <div className={`flex-shrink-0 ${tagCls}`}>
                    {moneyFull(e.amount)} · {tag}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-indie-navy/10 text-[11px] text-indie-ink/50 leading-relaxed">
        The reconciliation sheet is a LawPay-wide inflow log, not a pure IOLTA ledger. Only rows with a Purpose classified are true trust liability; unclassified rows are typically earned fees routed to operating accounts. Update the QBO IOLTA 3209 balance above whenever you pull a fresh balance sheet.
      </div>
      {err && <div data-testid="iolta-error" className="mt-3 text-xs text-indie-neg-dark">{err}</div>}
    </div>
  );
}
